import diplomaLockedIcon from "assets/icons/diplomaLocked.svg"
import diplomaUnlockedIcon from "assets/icons/diplomaUnlocked.svg"
import { useInfiniteGraphqlQuery } from "graphql-mock"
import { last, times } from "lodash-es"
import { ErrorFallback } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { FiestaLink } from "utility-components"
import { UserProfileView } from ".."
import { query } from "./gql"

import s from "./styles.module.scss"

const ITEMS_PER_PAGE = 6

export const UserProfileCertificatesConnected = () => {
  const { data, error, isPending, isFetchingNextPage, fetchNextPage } =
    useInfiniteGraphqlQuery(
      query,
      { limit: ITEMS_PER_PAGE },
      {
        getNextPage: (data) => data.products.pageInfo.nextPage,
      }
    )

  const products = data?.pages.flatMap((page) => page.products.nodes) ?? []
  const pageInfo = last(data?.pages)?.products.pageInfo

  // We don't know here yet if company has certificates or not.
  // There is more companies that don't use certificates, so we don't show
  // the loading state for this component to avoid layout shift.

  if (isPending || !products.length) return null

  return (
    <UserProfileCertificates
      error={error}
      products={products}
      pageInfo={pageInfo}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
    />
  )
}

type Product = {
  id: string
  progress: { certified: boolean }
  diplomaConfiguration: { title: string | null } | null
}
type PageInfo =
  | {
      hasNextPage: boolean | null
      nextPage: number | null
      nextPageItemCount: number
    }
  | undefined

type UserProfileCertificatesProps = {
  error: unknown
  products: Product[]
  pageInfo: PageInfo
  isFetchingNextPage: boolean
  fetchNextPage: () => void
}
export const UserProfileCertificates = ({
  error,
  products,
  pageInfo,
  isFetchingNextPage,
  fetchNextPage,
}: UserProfileCertificatesProps) => {
  const { t } = useTranslation()

  if (error) return <UserProfileCertificateError error={error} />

  const hasMoreCertificates = pageInfo?.hasNextPage && !isFetchingNextPage

  return (
    <UserProfileView.Section title={t("userProfile.SECTION_CERTIFICATES")}>
      <div className={s.certificates}>
        {products.map((product) => (
          <Certificate key={product.id} product={product} />
        ))}
        {isFetchingNextPage && (
          <CertificateSkeleton count={pageInfo?.nextPageItemCount} />
        )}
      </div>
      {hasMoreCertificates && <ShowMore onClick={fetchNextPage} />}
    </UserProfileView.Section>
  )
}

export const UserProfileCertificateError = ({ error }: { error: unknown }) => {
  const { t } = useTranslation()

  return (
    <UserProfileView.Section title={t("userProfile.SECTION_CERTIFICATES")}>
      <ErrorFallback
        error={error}
        title={t("error.GENERIC")}
        message={t("error.COULD_NOT_LOAD")}
      />
    </UserProfileView.Section>
  )
}

const Certificate = ({ product }: { product: Product }) => {
  const { id, diplomaConfiguration, progress } = product
  const isUnlocked = progress.certified

  return (
    <div className={s.certificate}>
      <FiestaLink to={`diploma/${id}`} className={s.certificate__content}>
        <div className={modifiers(s, "certificate__diploma", { isUnlocked })}>
          {isUnlocked ? (
            <img src={diplomaUnlockedIcon} alt="Unlocked" />
          ) : (
            <img src={diplomaLockedIcon} alt="Locked" />
          )}
        </div>
        <div className={modifiers(s, "certificate__title", { isUnlocked })}>
          {diplomaConfiguration?.title}
        </div>
      </FiestaLink>
    </div>
  )
}

const CertificateSkeleton = ({ count }: { count?: number }) => {
  return times(count ?? ITEMS_PER_PAGE, () => (
    <div className={s.certificate}>
      <div className={s.certificate__content}>
        <div className={s.skeleton__diploma} />
        <div className={s.skeleton__title} />
      </div>
    </div>
  ))
}

const ShowMore = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation()

  return (
    <button className={s.showMore} onClick={onClick}>
      {t("userProfile.SHOW_MORE")}
    </button>
  )
}
